import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { curveCatmullRom, line, max as d3max, scaleLinear } from 'd3';

/** Keys of T whose value type is assignable to V (ignoring null/undefined). */
type KeysOfType<T, V> = {
  [K in keyof T]-?: NonNullable<T[K]> extends V ? K : never;
}[keyof T];

type NumberKey<T> = KeysOfType<T, number>;
type StringKey<T> = KeysOfType<T, string>;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 9327412 -> "9.33m", 5331 -> "5.33k", 481.034 -> "481.03" */
export function compact(n: number): string {
  const abs = Math.abs(n);
  const [div, suffix] =
    abs >= 1e12 ? [1e12, 't'] :
    abs >= 1e9  ? [1e9,  'b'] :
    abs >= 1e6  ? [1e6,  'm'] :
    abs >= 1e3  ? [1e3,  'k'] : [1, ''];
  return (n / div).toFixed(2) + suffix;
}

/** Counts: whole numbers below 1000, compact above. */
export function compactCount(n: number): string {
  return Math.abs(n) < 1000 ? String(Math.round(n)) : compact(n);
}

/** One entry of the `transactions` jsonb column. */
export interface TipTransaction {
  id: string;
  amount: number;
  time: string;
  tz?: string;
}

interface Pt {
  i: number;
  label: string;
  v1: number;
  v2: number;
  x: number;
  bandX: number;
  bandW: number;
  barX: number;
  barY: number;
  barW: number;
  barH: number;
  dotY: number;
  reversals: number | null;
  p25: number | null;
  median: number | null;
  p75: number | null;
  txns: TipTransaction[] | null;
}

interface TipRow { label: string; value: string; y: number; }

@Component({
  selector: 'app-dual-axis-chart',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dual-axis-bar-chart.component.html',
  styles: [`
    :host { display: block; width: 100%; }
    svg { display: block; width: 100%; height: auto; font-family: inherit; }
    .grid { stroke: #e6ebef; stroke-width: 1; }
    .tick { font-size: 11px; }
    .xlabel { font-size: 11px; fill: #6b7785; }
    .axis-title { font-size: 11px; }
    .legend { font-size: 12px; fill: #46525e; }
    .hover-band { cursor: crosshair; }
    .tip { pointer-events: none; }
    .tip rect.card { fill: #fff; stroke: #dde5eb; filter: drop-shadow(0 2px 6px rgba(20,40,60,.16)); }
    .tip-rule { stroke: #eef2f5; }
    .tip-title { font-size: 12.5px; font-weight: 600; fill: #22303c; }
    .tip-label { font-size: 11.5px; fill: #5b6874; }
    .tip-val { font-size: 11.5px; font-variant-numeric: tabular-nums; }
    .tip-section { font-size: 10px; fill: #93a1ad; letter-spacing: .04em; }
    .tip-row-label { font-size: 11px; fill: #5b6874; }
    .tip-row-val { font-size: 11px; fill: #22303c; font-variant-numeric: tabular-nums; }
    svg { display: block; width: 100%; height: auto; font-family: inherit; overflow: visible; } // lets tooltips hang outside of chart
  `],
})
export class DualAxisChartComponent<T extends object> {
  // Signal inputs: read them as values() / label1() / color1() everywhere,
  // including in the template.

  values = input.required<T[]>();
  property1 = input.required<NumberKey<T>>();
  property2 = input.required<NumberKey<T>>();
  xLabelProperty = input.required<StringKey<T>>();

  /** Optional: numeric x positions. Omit for even spacing. */
  xValuesProperty = input<NumberKey<T> | null>(null);

  /** Optional: key holding returns/reversals. Adds a third row to the tooltip header. */
  reversalsProperty = input<NumberKey<T> | null>(null);
  reversalsLabel = input('Returns');

  /** Optional: keys holding the quartiles. Shown in the tooltip when there is no list. */
  p25Property = input<NumberKey<T> | null>(null);
  medianProperty = input<NumberKey<T> | null>(null);
  p75Property = input<NumberKey<T> | null>(null);

  /** Optional: key holding TipTransaction[]. Listed in the tooltip when populated. */
  transactionsProperty = input<KeysOfType<T, TipTransaction[]> | null>(null);

  statsHeading = input('TRANSACTION SIZE');
  listHeading = input('TRANSACTIONS');

  label1 = input('');
  label2 = input('');

  /**
   * Any CSS colour. Pass design tokens with a fallback — "var(--x, #1f7a4d)" —
   * so an unreachable variable degrades to a visible colour instead of black.
   */
  color1 = input('var(--colorBadgeActiveCore)');
  color2 = input('var(--colorBadgePendingFull)');
  color3 = input('var(--colorBadgeFailedCore)');

  /** List transactions as "Jan 06, 10:52" rather than "10:52". */
  fullDates = input(true);

  format1 = input<(n: number) => string>(compact);
  format2 = input<(n: number) => string>(compactCount);

  /** Width divided by height. 2.6 is a wide dashboard band; 1.6 is chunkier. */
  ratio = input(2.6);

  /** Fixed coordinate space. Width is always 1000 user units; height follows the ratio. */
  readonly VW = 1000;
  readonly m = { top: 26, right: 58, bottom: 72, left: 58 };

  readonly hovered = signal<number | null>(null);

  readonly vh = computed(() => Math.round(this.VW / Math.max(0.3, this.ratio() || 2.6)));
  readonly iw = computed(() => this.VW - this.m.left - this.m.right);
  readonly ih = computed(() => Math.max(40, this.vh() - this.m.top - this.m.bottom));

  /** x centre for each datum: from xValuesProperty if given, else equally spaced. */
  private xs = computed(() => {
    const data = this.values() ?? [];
    const n = data.length;
    const iw = this.iw();
    if (!n) return [] as number[];

    const key = this.xValuesProperty();
    if (key) {
      const raw = data.map(d => Number(d[key]));
      const lo = Math.min(...raw), hi = Math.max(...raw);
      if (hi === lo) return raw.map(() => iw / 2);
      const pad = iw / (n * 2);
      const s = scaleLinear().domain([lo, hi]).range([pad, iw - pad]);
      return raw.map(s);
    }
    const step = iw / n;
    return data.map((_, i) => step * (i + 0.5));
  });

  /** Bars stay narrower than the gap between them, and fatten up as the count drops. */
  private barW = computed(() => {
    const xs = this.xs();
    const n = xs.length;
    if (!n) return 0;
    const gap = n > 1 ? Math.min(...xs.slice(1).map((v, i) => v - xs[i])) : this.iw();
    const ratio = Math.max(0.30, Math.min(0.45, 0.45 - (n - 6) * 0.008));
    return Math.max(3, Math.min(gap * ratio, 56));
  });

  private y1 = computed(() => {
    const hi = d3max(this.values() ?? [], d => Number(d[this.property1()])) ?? 0;
    return scaleLinear().domain([0, hi || 1]).nice(5).range([this.ih(), 0]);
  });

  private y2 = computed(() => {
    const hi = d3max(this.values() ?? [], d => Number(d[this.property2()])) ?? 0;
    return scaleLinear().domain([0, (hi || 1) * 1.15]).nice(5).range([this.ih(), 0]);
  });

  readonly ticks1 = computed(() => {
    const s = this.y1(), f = this.format1();
    return s.ticks(5).map(v => ({ v, y: s(v), text: f(v) }));
  });

  readonly ticks2 = computed(() => {
    const s = this.y2(), f = this.format2();
    return s.ticks(5).map(v => ({ v, y: s(v), text: f(v) }));
  });

  readonly pts = computed<Pt[]>(() => {
    const data = this.values() ?? [];
    const xs = this.xs();
    if (!xs.length) return [];
    const bw = this.barW(), y1 = this.y1(), y2 = this.y2(), ih = this.ih(), iw = this.iw();
    const p1 = this.property1(), p2 = this.property2(), lx = this.xLabelProperty();
    const rk = this.reversalsProperty();
    const qa = this.p25Property(), qb = this.medianProperty(), qc = this.p75Property();
    const tk = this.transactionsProperty();

    const num = (d: T, k: NumberKey<T> | null): number | null => {
      if (!k) return null;
      const raw = d[k];
      if (raw === null || raw === undefined || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };

    return data.map((d, i) => {
      const v1 = Number(d[p1]) || 0;
      const v2 = Number(d[p2]) || 0;
      const x = xs[i];
      const x0 = i === 0 ? 0 : (xs[i - 1] + x) / 2;
      const x1 = i === xs.length - 1 ? iw : (x + xs[i + 1]) / 2;
      const barY = y1(v1);
      return {
        i, label: String(d[lx] ?? ''), v1, v2, x,
        bandX: x0, bandW: Math.max(0, x1 - x0),
        barX: x - bw / 2, barY, barW: bw, barH: Math.max(0, ih - barY),
        dotY: y2(v2),
        reversals: num(d, rk),
        p25: num(d, qa),
        median: num(d, qb),
        p75: num(d, qc),
        txns: tk ? (d[tk] as TipTransaction[] | null) ?? null : null,
      };
    });
  });

  readonly path = computed(() => {
    const pts = this.pts();
    if (pts.length < 2) return '';
    return line<Pt>().x(p => p.x).y(p => p.dotY).curve(curveCatmullRom.alpha(0.5))(pts) ?? '';
  });

  readonly rotate = computed(() => {
    const n = this.pts().length;
    return n > 1 && this.iw() / n < 56;
  });

  readonly hoveredPt = computed(() => {
    const i = this.hovered();
    return i == null ? null : this.pts()[i] ?? null;
  });

  readonly legend = computed(() => {
    const a = this.label1(), b = this.label2();
    const wa = 15 + this.textW(a, 12), wb = 15 + this.textW(b, 12);
    const start = (this.VW - (wa + 28 + wb)) / 2;
    return [
      { label: a, color: this.color1(), x: start },
      { label: b, color: this.color2(), x: start + wa + 28 },
    ];
  });

  readonly tip = computed(() => {
    const p = this.hoveredPt();
    if (!p) return null;

    const f1 = this.format1(), f2 = this.format2();
    const v1 = f1(p.v1), v2 = f2(p.v2);
    const rowW = (l: string, v: string) => 27 + this.textW(l, 11.5) + 20 + this.textW(v, 11.5) + 12;

    // --- header: two rows always, plus an optional returns row -------------
    const showReturns = this.reversalsProperty() !== null && !!p.reversals;
    const retLabel = this.reversalsLabel();
    const retVal = showReturns ? f1(p.reversals as number) : '';
    const headerH = showReturns ? 94 : 76;

    let w = Math.max(140, Math.max(
      this.textW(p.label, 12.5) + 24,
      rowW(this.label1(), v1),
      rowW(this.label2(), v2),
      showReturns ? rowW(retLabel, retVal) : 0,
    ));

    // Transaction list wins when present; quartiles are the fallback.
    const txns = p.txns?.length ? p.txns : null;
    const quart: [string, number][] = [];
    if (!txns) {
      if (p.p25 !== null) quart.push(['25th percentile', p.p25]);
      if (p.median !== null) quart.push(['Median', p.median]);
      if (p.p75 !== null) quart.push(['75th percentile', p.p75]);
    }

    let h = headerH;
    let ruleY = 0, sectionY = 0;
    let heading = '';
    let rows: TipRow[] | null = null;

    const pairs: [string, string][] = txns
      ? txns.map(t => [this.clock(t.time), f1(Number(t.amount) || 0)] as [string, string])
      : quart.map(([label, value]) => [label, f1(value)] as [string, string]);

    if (pairs.length) {
      const built = pairs.map(([label, value], i) => ({ label, value, y: headerH + 34 + i * 16 }));
      w = Math.max(w, 200, ...built.map(r => 24 + this.textW(r.label, 11) + 20 + this.textW(r.value, 11)));
      heading = txns ? this.listHeading() : this.statsHeading();
      rows = built;
      ruleY = headerH;
      sectionY = headerH + 16;
      h = headerH + 34 + built.length * 16 + 2;
    }

    w = Math.min(340, w);
    const x = Math.max(4, Math.min(this.VW - w - 4, p.x + this.m.left - w / 2));
    return {
      x, y: 4, w, h, title: p.label, v1, v2,
      showReturns, retLabel, retVal,
      rows, heading, ruleY, sectionY,
    };
  });

  /** "2026-09-02T09:54:35" -> "09:54", or "Sep 02, 09:54" when fullDates is on. */
  private clock(s: string): string {
    const [date, time] = (s ?? '').split('T');
    const hhmm = time ? time.slice(0, 5) : '';
    if (!this.fullDates() || !date) return hhmm || (s ?? '');

    const [, mm, dd] = date.split('-');
    const month = MONTHS[Number(mm) - 1] ?? mm;
    return hhmm ? `${month} ${dd}, ${hhmm}` : `${month} ${dd}`;
  }

  /** Rough advance-width estimate; good enough to size a tooltip box. */
  private textW(s: string, size: number): number {
    return s.length * size * 0.56;
  }
}